import React, { useEffect, useState } from "react";
import "style/NavBar.scss";
import { Link, useLocation, useNavigate } from "react-router-dom";

import legacy from "config/i18n/legacy";
import landing from "config/i18n/landing";
import LangMenu from "components/LangMenu";
import ui from "config/i18n/ui";
import useLang from "hooks/useLang";
import useJamaLang from "hooks/useJamaLang";
import { isLang, SITE_LANGS } from "config/i18n/languages";
import { JAMA_LANGS } from "config/i18n/jamaLangs";
import { FORCED_LANG } from "config/forcedLang";

import { default as SvgHeaderTitle } from "assets/SvgHeaderTitle.svg";

/** Routes whose hero starts at the very top, so the bar starts transparent. */
const HERO_ROUTES = [
  "/",
  "/marksim",
  "/ms-trader",
  "/ms-trader/architecture",
  "/jama",
  "/hiring",
];

interface NavLinkItem {
  to: string;
  label: string;
  exact?: boolean;
  /** Extra class, e.g. the highlighted "We are hiring" entry. */
  modifier?: string;
}

const NavBar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const lang = useLang();
  const nav = landing[lang].nav;
  const language = legacy[lang];
  const aria = ui[lang].aria;
  const navigate = useNavigate();
  // The JAMA page offers all 23 judojama.com languages; the extra ones only
  // switch that page's content (?lang=…), not the site language.
  const onJama = location.pathname === "/jama";
  const jamaLang = useJamaLang();

  // Pin the resolved language for the session (all components read it).
  useEffect(() => {
    try {
      if (sessionStorage.getItem("lang") !== lang) {
        sessionStorage.setItem("lang", lang);
      }
    } catch {
      /* storage blocked: every component resolves the same fallback */
    }
  }, [lang]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  const switchLanguage = (lang: string) => {
    if (lang !== sessionStorage.getItem("lang")) {
      sessionStorage.setItem("lang", lang);
      window.location.reload();
    }
  };

  const selectJamaLanguage = (code: string) => {
    if (!isLang(code)) {
      navigate(`/jama?lang=${code}`);
    } else if (code === lang) {
      navigate("/jama");
    } else {
      sessionStorage.setItem("lang", code);
      window.location.assign("/jama");
    }
  };

  const links: NavLinkItem[] = [
    { to: "/", label: language.dropdown.overview, exact: true },
    { to: "/marksim", label: nav.marksim },
    { to: "/ms-trader", label: nav.mstrader },
    { to: "/jama", label: nav.jama },
    { to: "/contact", label: language.dropdown.contact },
    { to: "/hiring", label: nav.hiring, modifier: "hiring" },
  ];

  const isActive = (link: NavLinkItem) =>
    link.exact
      ? location.pathname === link.to
      : location.pathname === link.to ||
        location.pathname.startsWith(`${link.to}/`);

  const transparent =
    HERO_ROUTES.includes(location.pathname) && !scrolled && !open;

  return (
    <nav
      className={`navbar${transparent ? " navbar--transparent" : ""}${
        open ? " navbar--open" : ""
      }`}
    >
      <div className="navbar__inner">
        <Link to="/" className="navbar__brand" aria-label={aria.home}>
          <img src={SvgHeaderTitle} alt="MARKSIM" />
        </Link>

        <div className="navbar__links">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`navbar__link${
                link.modifier ? ` navbar__link--${link.modifier}` : ""
              }${isActive(link) ? " is-active" : ""}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {!FORCED_LANG && (
          <LangMenu
            langs={onJama ? JAMA_LANGS : SITE_LANGS}
            current={onJama ? jamaLang : lang}
            label={aria.language}
            onSelect={onJama ? selectJamaLanguage : switchLanguage}
          />
        )}

        <button
          type="button"
          className="navbar__burger"
          aria-label={aria.menu}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <div className="navbar__panel">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={`navbar__panel-link${
              link.modifier ? ` navbar__panel-link--${link.modifier}` : ""
            }${isActive(link) ? " is-active" : ""}`}
          >
            {link.label}
          </Link>
        ))}
        <Link
          to="/imprint"
          className={`navbar__panel-link${
            location.pathname === "/imprint" ? " is-active" : ""
          }`}
        >
          {language.dropdown.imprint}
        </Link>
      </div>
    </nav>
  );
};

export default NavBar;
